import { useCallback, useState } from "react";

import { Modal } from "../Modal";
import styles from "./FeaturesModal.module.css";

const featureCategories = [
  {
    title: "Voice Calls",
    features: [
      "End-to-end encrypted voice calls via Matrix + LiveKit",
      "HD audio with Opus codec",
      "Mute/unmute microphone",
      "Push-to-talk mode (hold Spacebar)",
      "Loudspeaker / earpiece toggle",
      "Audio input device selection (microphone)",
      "Audio output device selection (speaker)",
      "Sound effects volume control",
      "Echo cancellation toggle",
      "Noise suppression toggle",
      "Auto gain control toggle",
    ],
  },
  {
    title: "Call Management",
    features: [
      "Create instant call rooms",
      "Join existing calls by room name or link",
      "Invite participants via share link + QR code",
      "Call timer — see elapsed call duration",
      "Recent calls list for quick re-dial",
      "Screen sharing with audio",
      "Picture-in-Picture (PiP) mode",
      "Fullscreen mode (F key)",
      "Keyboard shortcuts for all actions (? key)",
    ],
  },
  {
    title: "Reactions & Interaction",
    features: [
      "Raise hand indicator",
      "Animated on-screen reactions",
      "Sound effects for interactions (toggleable)",
    ],
  },
  {
    title: "Security & Privacy",
    features: [
      "End-to-end encryption (E2EE) via Matrix Megolm",
      "Shared key encryption for room calls",
      "No AI processing — everything runs locally in your browser",
      "No tracking, no data collection, no analytics by default",
      "Fully open source (AGPL-3.0)",
      "Self-hostable backend for complete control",
    ],
  },
  {
    title: "Customization & Settings",
    features: [
      "Background blur toggle",
      "Grid / Spotlight layout switch",
      "Show/hide participant name tags",
      "Show/hide speaking indicators",
      "Developer mode with advanced debugging tools",
      "Connection statistics overlay (RTT, jitter, packet loss)",
      "Duplicate tiles for testing layouts",
    ],
  },
  {
    title: "Audio Processing",
    features: [
      "Built-in echo cancellation (Acoustic Echo Cancellation)",
      "Built-in noise suppression (background noise filtering)",
      "Built-in auto gain control (automatic volume adjustment)",
      "All audio processing is free and runs locally",
      "No cloud processing, no API calls, no latency",
    ],
  },
  {
    title: "Platform Support",
    features: [
      "Works in all modern browsers (Chrome, Firefox, Safari, Edge)",
      "Responsive design — desktop, tablet, and mobile",
      "Touch-optimized bottom sheets on phones",
      "Native app integration via iOS and Android wrappers",
      "Widget mode for embedding in Matrix clients",
      "Wake lock — keeps screen on during calls",
      "Dark theme support",
    ],
  },
  {
    title: "Performance & Reliability",
    features: [
      "Built with Rust (WASM) cryptography for fast E2EE",
      "LiveKit SFU for scalable multi-party calls",
      "Reconnection handling on network loss",
      "Optimized WebRTC stack for low latency",
      "No external dependencies for core functionality",
      "Everything runs client-side in your browser",
    ],
  },
];

export const FeaturesModal = ({
  open,
  onDismiss,
}: {
  open: boolean;
  onDismiss: () => void;
}) => (
  <Modal
    title="Nevai Coffe Features"
    open={open}
    onDismiss={onDismiss}
    className={styles.featuresModal}
    classNameModal={styles.featuresModalDialog}
  >
    <div className={styles.content}>
      <div className={styles.grid}>
        {featureCategories.map((category) => (
          <div key={category.title} className={styles.category}>
            <h3 className={styles.categoryTitle}>{category.title}</h3>
            <ul className={styles.featureList}>
              {category.features.map((feature) => (
                <li key={feature} className={styles.featureItem}>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </Modal>
);

export const useFeaturesModal = () => {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  return {
    featuresOpen: open,
    openFeatures: openModal,
    closeFeatures: closeModal,
  };
};

export const FeaturesModalFeatures = () => (
  <div className={styles.content}>
    <div className={styles.grid}>
      {featureCategories.map((category) => (
        <div key={category.title} className={styles.category}>
          <h3 className={styles.categoryTitle}>{category.title}</h3>
          <ul className={styles.featureList}>
            {category.features.map((feature) => (
              <li key={feature} className={styles.featureItem}>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);
